import React, { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Stack, Select, MenuItem, Button, Table, TableHead, TableRow, TableCell, TableBody,
  Checkbox, FormControlLabel, Alert, CircularProgress, Skeleton, Link, ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import * as XLSX from 'xlsx';
import { exportsApi } from '../services/api';
import PeriodPicker from './common/PeriodPicker';
import InfoTooltip from './common/InfoTooltip';
import EmptyState from './common/EmptyState';
import { ErrorState } from './common/States';
import { presetPeriod, periodQuery } from '../utils/period';
import { apiError, formatDate, formatMoney, formatNumber, formatPercent, mskToday } from '../utils/format';
import ExportGate from './common/ExportGate';
import { tokens } from '../theme';

const GROUPS = [
  { key: 'document', label: 'Документ и даты' },
  { key: 'product', label: 'Товар' },
  { key: 'financial', label: 'Суммы и количество' },
];
const PREVIEW_ROWS = 50;
const COLS_KEY = 'marqit_builder_columns_v1';

const readSaved = () => {
  try {
    return JSON.parse(window.localStorage.getItem(COLS_KEY)) || {};
  } catch (e) {
    return {};
  }
};

// Excel cell: numbers stay numbers, dates stay readable text.
const cellValue = (kind, v) => {
  if (v == null) return '';
  if (kind === 'money' || kind === 'int' || kind === 'pct') return Number(v);
  if (kind === 'bool') return v ? 'да' : 'нет';
  if (kind === 'datetime') return String(v).replace('T', ' ');
  return v;
};

const show = (kind, v) => {
  if (v == null || v === '') return '—';
  switch (kind) {
    case 'money': return formatMoney(v, { kopecks: true });
    case 'int': return formatNumber(v);
    case 'pct': return formatPercent(v);
    case 'bool': return v ? 'да' : 'нет';
    case 'date': return formatDate(v);
    case 'datetime': return `${formatDate(String(v).slice(0, 10))} ${String(v).slice(11, 16)}`; // Moscow wall time from WB
    default: return v;
  }
};

// Report builder: row-level WB reports for any loaded period, from data MARQIT already stores.
const ReportBuilder = ({ accounts }) => {
  const [types, setTypes] = useState(null);
  const [typesError, setTypesError] = useState('');
  const [type, setType] = useState('realization');
  const [accountId, setAccountId] = useState(accounts.length === 1 ? accounts[0].id : '');
  const [period, setPeriod] = useState(presetPeriod(30));
  const [chosen, setChosen] = useState(readSaved);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    exportsApi.types().then((r) => setTypes(r.data.types)).catch((e) => setTypesError(apiError(e)));
  }, []);

  const spec = useMemo(() => types?.find((t) => t.type === type), [types, type]);
  const columns = useMemo(() => {
    if (!spec) return [];
    const saved = chosen[type];
    return Array.isArray(saved) && saved.length ? saved : spec.columns.filter((c) => c.default).map((c) => c.key);
  }, [spec, chosen, type]);

  const setColumns = (next) => {
    const all = { ...chosen, [type]: next };
    setChosen(all);
    try {
      window.localStorage.setItem(COLS_KEY, JSON.stringify(all));
    } catch (e) {
      // not persisted in private mode
    }
  };
  const toggle = (key) => {
    // keep the order of the report's column list
    const set = new Set(columns.includes(key) ? columns.filter((k) => k !== key) : [...columns, key]);
    setColumns(spec.columns.map((c) => c.key).filter((k) => set.has(k)));
  };

  useEffect(() => { setResult(null); setError(''); }, [type, accountId, period, columns]);

  const run = async () => {
    setLoading(true);
    setError('');
    try {
      const params = {
        type, columns: columns.join(','), ...(accountId ? { account_id: accountId } : {}),
        ...(spec.snapshot ? {} : periodQuery(period)),
      };
      const { data } = await exportsApi.build(params);
      setResult(data);
    } catch (e) {
      setError(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  const download = () => {
    const header = result.columns.map((c) => c.label);
    const body = result.rows.map((row) => row.map((v, i) => cellValue(result.columns[i].kind, v)));
    const ws = XLSX.utils.aoa_to_sheet([header, ...body]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, spec.label.slice(0, 31));
    const suffix = spec.snapshot ? mskToday() : `${result.period.from}_${result.period.to}`;
    XLSX.writeFile(wb, `marqit_wb_${type}_${suffix}.xlsx`);
  };

  if (typesError) return <ErrorState message={typesError} />;
  if (!types) return <Skeleton variant="rounded" height={320} sx={{ borderRadius: '12px' }} />;

  const shortCoverage = (result?.coverage || []).filter((c) => !spec.snapshot && result.period
    && (!c.from || c.from > result.period.from || (c.to && c.to < result.period.to)));

  return (
    <Stack spacing={3}>
      <Card>
        <CardContent>
          <Typography variant="h3" component="h2" sx={{ mb: 0.5 }}>Конструктор отчётов</Typography>
          <Typography color="text.secondary" sx={{ mb: 2.5 }}>
            Выберите вид отчёта, период и колонки — MARQIT соберёт его из уже загруженных данных за несколько секунд, без запросов к Wildberries.
          </Typography>

          <ToggleButtonGroup exclusive value={type} onChange={(_, v) => v && setType(v)} aria-label="Вид отчёта"
            sx={{ flexWrap: 'wrap', mb: 1, '& .MuiToggleButton-root': { borderRadius: '10px !important', px: 2 } }}>
            {types.map((t) => <ToggleButton key={t.type} value={t.type}>{t.label}</ToggleButton>)}
          </ToggleButtonGroup>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>{spec.description}</Typography>

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'flex-start' }} sx={{ mb: 2.5 }}>
            {accounts.length > 1 && (
              <Select size="small" value={accountId || 'all'} onChange={(e) => setAccountId(e.target.value === 'all' ? '' : e.target.value)}
                inputProps={{ 'aria-label': 'Кабинет' }} sx={{ minWidth: 220 }}>
                <MenuItem value="all">Все кабинеты</MenuItem>
                {accounts.map((a) => <MenuItem key={a.id} value={a.id}>{a.name}</MenuItem>)}
              </Select>
            )}
            {spec.snapshot
              ? <Typography variant="body2" color="text.secondary" sx={{ pt: 1 }}>Остатки — текущий снимок, период не нужен.</Typography>
              : <PeriodPicker value={period} onChange={setPeriod} />}
          </Stack>

          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Колонки: {columns.length} из {spec.columns.length}
            <Button size="small" sx={{ ml: 1 }} onClick={() => setColumns(spec.columns.filter((c) => c.default).map((c) => c.key))}>По умолчанию</Button>
            <Button size="small" onClick={() => setColumns(spec.columns.map((c) => c.key))}>Все</Button>
          </Typography>
          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, mb: 2.5 }}>
            {GROUPS.map((g) => {
              const list = spec.columns.filter((c) => c.group === g.key);
              if (!list.length) return null;
              return (
                <Box key={g.key} sx={{ p: 1.5, borderRadius: '12px', border: `1px solid ${tokens.border}` }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>{g.label}</Typography>
                  {list.map((c) => (
                    <FormControlLabel key={c.key} sx={{ display: 'flex', m: 0 }}
                      control={<Checkbox size="small" checked={columns.includes(c.key)} onChange={() => toggle(c.key)} sx={{ py: 0.25 }} />}
                      label={<Typography variant="body2">{c.label}</Typography>} />
                  ))}
                </Box>
              );
            })}
          </Box>

          <Stack direction="row" spacing={1.5}>
            <Button variant="contained" onClick={run} disabled={loading || !columns.length}
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <PlayArrowRoundedIcon />}>
              Сформировать
            </Button>
            <ExportGate>
              <Button variant="outlined" onClick={download} disabled={!result?.rows.length} startIcon={<FileDownloadOutlinedIcon />}>
                Скачать Excel
              </Button>
            </ExportGate>
          </Stack>
        </CardContent>
      </Card>

      {error && <ErrorState message={error} compact />}

      {result && (
        <Card>
          <CardContent>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
              <Typography variant="h3" component="h2">
                {spec.label}: {formatNumber(result.total)} {result.total === 1 ? 'строка' : 'строк'}
              </Typography>
              <InfoTooltip exclamation size={16}
                title="Строки берутся из данных, которые MARQIT загрузил из кабинета. Если за часть периода данных ещё нет, ниже будет пометка; более раннюю историю можно запросить на странице «Состояние загрузки»." />
            </Stack>
            {result.truncated && (
              <Alert severity="warning" sx={{ mb: 1.5 }}>
                Показаны первые {formatNumber(result.total)} строк — это предел одной выгрузки. Разбейте период на части.
              </Alert>
            )}
            {shortCoverage.map((c) => (
              <Alert key={c.account_id} severity="info" sx={{ mb: 1.5 }}>
                {c.account_name}: {c.from
                  ? `данные загружены с ${formatDate(c.from)}${c.to ? ` по ${formatDate(c.to)}` : ''}${c.backfilling ? ', идёт догрузка более ранней истории' : ''}.`
                  : 'данные ещё загружаются.'}
                {' '}
                <Link component={RouterLink} to={`/marketplaces/sync/${c.account_id}`}>Состояние загрузки</Link>
              </Alert>
            ))}
            {result.rows.length === 0 ? (
              <EmptyState title="Строк нет" description="За выбранный период таких операций нет в загруженных данных." />
            ) : (
              <>
                <Box sx={{ overflowX: 'auto', border: `1px solid ${tokens.border}`, borderRadius: '12px' }}>
                  <Table size="small" stickyHeader aria-label="Предпросмотр отчёта" sx={{ '& td, & th': { whiteSpace: 'nowrap' } }}>
                    <TableHead>
                      <TableRow>
                        {result.columns.map((c) => (
                          <TableCell key={c.key} align={['money', 'int', 'pct'].includes(c.kind) ? 'right' : 'left'}>{c.label}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {result.rows.slice(0, PREVIEW_ROWS).map((row, i) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <TableRow key={i} hover>
                          {row.map((v, j) => (
                            <TableCell key={result.columns[j].key} align={['money', 'int', 'pct'].includes(result.columns[j].kind) ? 'right' : 'left'}
                              sx={result.columns[j].kind === 'money' && Number(v) < 0 ? { color: tokens.negative } : undefined}>
                              {show(result.columns[j].kind, v)}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
                {result.rows.length > PREVIEW_ROWS && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    Показаны первые {PREVIEW_ROWS} строк. Весь отчёт — в файле Excel.
                  </Typography>
                )}
              </>
            )}
          </CardContent>
        </Card>
      )}
    </Stack>
  );
};

export default ReportBuilder;
